import React from 'react'
import { WarningIcon } from '@chakra-ui/icons'
import { Box, HStack, BoxProps, TextProps } from '@chakra-ui/react'
import {
    Popover,
    PopoverTrigger,
    PopoverArrow,
    PopoverContent,
    PopoverBody,
} from '@chakra-ui/react'
import {
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    MenuButtonProps,
    MenuProps,
} from '@chakra-ui/react'

import SharedLabel from './shared-label'
import { CheckIcon, ChevronDownIcon } from '@chakra-ui/icons'
import { Button, ButtonProps } from '@chakra-ui/react'
import { Flex, MenuListProps, useDisclosure } from '@chakra-ui/react'
import { adjustArrayWithItem } from '~utils/helpers'

interface Props extends ButtonProps {
    label?: string
    labelProps?: TextProps
    moreInfo?: React.ReactElement
    containerProps?: BoxProps
    menuProps?: MenuListProps
    menuOverrides?: Omit<MenuProps, 'isOpen' | 'onClose' | 'children'>
    placeholder?: string
    textModification?: (e: string) => string
    options?: string[] | { value: string; rightElement?: React.ReactElement }[]
    values: string[]
    setValues: (value: string[]) => void
    listMaxHeight?: string
    error?: React.ReactNode
    onEditComplete?: () => void
}

const MenuMultiselect: React.FC<Props> = ({
    label,
    labelProps,
    moreInfo,
    containerProps,
    menuProps,
    placeholder,
    textModification,
    options,
    values,
    setValues,
    listMaxHeight,
    error,
    onEditComplete,
    menuOverrides,
    ...props
}) => {
    const { isOpen, onOpen, onClose } = useDisclosure()

    return (
        <Box {...containerProps}>
            {(label || moreInfo) && (
                <Box mb={2}>
                    {label && (
                        <SharedLabel mb={1} {...labelProps}>
                            {label}
                        </SharedLabel>
                    )}
                    {!!moreInfo && moreInfo}
                </Box>
            )}
            <HStack alignItems="center">
                <Menu
                    isOpen={isOpen}
                    onClose={() => {
                        onClose()
                        onEditComplete?.()
                    }}
                    {...menuOverrides}
                >
                    <MenuButton
                        as={Button}
                        variant="outline"
                        rightIcon={<ChevronDownIcon />}
                        textAlign="left"
                        onClick={e => {
                            e.stopPropagation()
                            e.preventDefault()
                            onOpen()
                        }}
                        {...props}
                    >
                        {values.length
                            ? `${
                                  textModification
                                      ? textModification(values[0])
                                      : values[0]
                              } ${
                                  values.length > 1
                                      ? `+${values.length - 1}`
                                      : ''
                              }`
                            : placeholder || 'Select'}
                    </MenuButton>
                    <MenuList
                        maxH={listMaxHeight || '296px'}
                        overflowY="auto"
                        {...menuProps}
                    >
                        {options &&
                            options.map((o, index) => {
                                const curVal =
                                    typeof o === 'string' ? o : o.value
                                return (
                                    <MenuItem
                                        key={`${o}-${index}`}
                                        icon={
                                            values.includes(curVal) ? (
                                                <CheckIcon />
                                            ) : (
                                                <Box w="12px" />
                                            )
                                        }
                                        onClick={() =>
                                            setValues(
                                                adjustArrayWithItem(
                                                    values,
                                                    curVal,
                                                ),
                                            )
                                        }
                                    >
                                        <Flex align="center">
                                            <>
                                                {typeof o === 'string'
                                                    ? ''
                                                    : o.rightElement}
                                                {textModification
                                                    ? textModification(curVal)
                                                    : o}
                                            </>
                                        </Flex>
                                    </MenuItem>
                                )
                            })}
                    </MenuList>
                </Menu>
                {!!error && (
                    <Popover>
                        <PopoverTrigger>
                            <Box>
                                <WarningIcon boxSize={6} color="yellow.500" />
                            </Box>
                        </PopoverTrigger>
                        <PopoverContent>
                            <PopoverArrow />
                            <PopoverBody>{error}</PopoverBody>
                        </PopoverContent>
                    </Popover>
                )}
            </HStack>
        </Box>
    )
}

export default MenuMultiselect
